import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EasyPassingTrigger() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleTrigger = async () => {
    setProcessing(true);
    const token = localStorage.getItem('session_token');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/easy-passing/trigger/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(response.data);
      toast.success('Easy Passing process initiated successfully');
    } catch (error) {
      console.error('Trigger failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate Easy Passing');
      setProcessing(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5] p-6">
        <Card className="p-8 max-w-2xl w-full bg-white/90 backdrop-blur-md border border-green-100 shadow-xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-medium text-gray-800 mb-2">
              Easy Passing Completed
            </h1>
            <p className="text-gray-600">
              All beneficiaries have been notified
            </p>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between p-4 bg-[#EBF0F5] rounded-xl">
              <span className="text-gray-700">Whispers Sent</span>
              <span className="text-2xl font-medium text-[#2B4C7E]">{result.whispers_sent}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-[#EBF0F5] rounded-xl">
              <span className="text-gray-700">Funds Distributed</span>
              <span className="text-2xl font-medium text-[#2B4C7E]">{result.funds_distributed}</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-[#EBF0F5] rounded-xl">
              <span className="text-gray-700">Beneficiaries Notified</span>
              <span className="text-2xl font-medium text-[#2B4C7E]">{result.beneficiaries_notified}</span>
            </div>
          </div>

          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full py-6 hover:from-[#1E3A63] hover:to-[#1E3A63]"
          >
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5] p-6">
      <Card className="p-8 max-w-2xl w-full bg-white/90 backdrop-blur-md border border-red-100 shadow-xl">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-[#EAD9BF] rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-medium text-gray-800 mb-2">
            Easy Passing Trigger
          </h1>
          <p className="text-gray-600">
            This action will initiate the Easy Passing process
          </p>
        </div>

        <div className="bg-red-50 rounded-2xl p-6 mb-8">
          <h3 className="font-medium text-gray-800 mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            Important: This action cannot be undone
          </h3>
          <p className="text-gray-700 mb-4">
            By clicking the button below, you confirm that the account holder has passed away and you are initiating the Easy Passing process. This will:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-red-600 mr-2">•</span>
              <span>Send all scheduled whispers to designated beneficiaries</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 mr-2">•</span>
              <span>Distribute funds according to the account holder's wishes</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 mr-2">•</span>
              <span>Notify all beneficiaries</span>
            </li>
            <li className="flex items-start">
              <span className="text-red-600 mr-2">•</span>
              <span>Release important documents and information</span>
            </li>
          </ul>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              disabled={processing}
              className="w-full bg-gradient-to-r from-red-600 to-[#B3956B] text-white rounded-full py-6 hover:from-red-700 hover:to-[#9C7F55]"
              data-testid="trigger-btn"
            >
              <Send className="h-5 w-5 mr-2" />
              {processing ? 'Processing...' : 'Initiate Easy Passing'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently initiate the Easy Passing process and notify all beneficiaries.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleTrigger} className="bg-red-600 hover:bg-red-700">
                Yes, Initiate Easy Passing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="w-full mt-4 rounded-full py-6"
        >
          Cancel
        </Button>
      </Card>
    </div>
  );
}